import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { MessageSquare, Newspaper, Plus, Send, Undo2 } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { errorText } from '../i18n';
import { relativeTime } from '../lib/time';
import { Avatar, Spinner, toast } from '@vatman/ui';
import { docKeys, pagePath, pageTitle, refreshPages, type Page } from './model';
import './docs.css';

/*
 * News of a space (crates/server/src/docs/posts.rs): posts are pages outside the tree, drafts until
 * published. The space's tab, the home page's strip, and the bar over a post.
 */

export interface PostCard {
  id: string;
  title: string;
  icon: string | null;
  publishedAt: number | null;
  createdAt: number;
  cover: string | null;
  excerpt: string;
  author: { id: string; name: string; color: string } | null;
  spaceId: string;
  spaceName: string;
  comments: number;
}

const report = (e: unknown) => toast(errorText(e instanceof ApiError ? e.code : 'internal'));
export const newsKey = (space: string | null, drafts = false) => ['pages', 'news', space, drafts] as const;

/** A new draft in the space's news, opened at once. */
export function useNewPost() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  return async (spaceId: string) => {
    try {
      const p = await api.post<Page>('/pages', { spaceId, kind: 'post', title: '' });
      void qc.invalidateQueries({ queryKey: ['pages', 'news'] });
      navigate(pagePath(p.id));
    } catch (e) {
      report(e);
    }
  };
}

function Card({ p, showSpace }: { p: PostCard; showSpace?: boolean }) {
  const { t } = useTranslation();
  return (
    <Link to={pagePath(p.id)} className="pg-post">
      {p.cover && <img className="pg-post-cover" src={p.cover} alt="" loading="lazy" onError={(e) => e.currentTarget.remove()} />}
      <span className="pg-post-body">
        <span className="pg-post-title">
          {p.icon && !p.icon.startsWith('icon:') ? `${p.icon} ` : ''}
          {pageTitle(p, t('docs.untitled'))}
        </span>
        {p.excerpt && <span className="pg-post-excerpt">{p.excerpt}</span>}
        <span className="muted pg-post-meta">
          {p.author && <Avatar name={p.author.name} color={p.author.color} size={18} />}
          {p.author?.name}
          {' · '}
          {p.publishedAt ? relativeTime(p.publishedAt) : t('docs.news.draft')}
          {showSpace && ` · ${p.spaceName}`}
          {p.comments > 0 && (
            <>
              {' · '}
              <MessageSquare size={12} /> {p.comments}
            </>
          )}
        </span>
      </span>
    </Link>
  );
}

/** A space's "News" tab: published posts, and the drafts the person sees. */
export function SpaceNews({ spaceId, canCreate }: { spaceId: string; canCreate: boolean }) {
  const { t } = useTranslation();
  const newPost = useNewPost();
  const posts = useQuery({ queryKey: newsKey(spaceId), queryFn: () => api.get<{ posts: PostCard[] }>(`/spaces/${spaceId}/posts`) });
  const drafts = useQuery({ queryKey: newsKey(spaceId, true), queryFn: () => api.get<{ posts: PostCard[] }>(`/spaces/${spaceId}/posts?drafts=true`), enabled: canCreate });
  if (posts.isLoading) return <Spinner />;
  const list = posts.data?.posts ?? [];
  const mine = drafts.data?.posts ?? [];
  return (
    <>
      {mine.length > 0 && (
        <section className="pg-news-drafts">
          <div className="pg-block-label">{t('docs.news.drafts', { count: mine.length })}</div>
          <div className="pg-posts">
            {mine.map((p) => (
              <Card key={p.id} p={p} />
            ))}
          </div>
        </section>
      )}
      {list.length === 0 ? (
        <div className="empty-state">
          <h3>{t('docs.news.empty')}</h3>
          <div>{t('docs.news.emptyHint')}</div>
          {canCreate && (
            <button className="btn primary" onClick={() => void newPost(spaceId)}>
              <Plus size={16} /> {t('docs.news.new')}
            </button>
          )}
        </div>
      ) : (
        <div className="pg-posts">
          {list.map((p) => (
            <Card key={p.id} p={p} />
          ))}
        </div>
      )}
    </>
  );
}

/** The newest posts of my spaces, on the home page. */
export function NewsStrip() {
  const { t } = useTranslation();
  const feed = useQuery({ queryKey: newsKey(null), queryFn: () => api.get<{ posts: PostCard[] }>('/posts?limit=4'), staleTime: 60_000 });
  const list = feed.data?.posts ?? [];
  if (!list.length) return null;
  return (
    <section className="pg-news-strip">
      <h2 className="pg-news-heading">
        <Newspaper size={17} /> {t('docs.news.title')}
      </h2>
      <div className="pg-posts">
        {list.map((p) => (
          <Card key={p.id} p={p} showSpace />
        ))}
      </div>
    </section>
  );
}

/** Over a post: a draft with its "Publish", or when it was published. */
export function PostBar({ page, canEdit }: { page: Page; canEdit: boolean }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const set = async (publish: boolean) => {
    try {
      const fresh = publish ? await api.post<Page>(`/pages/${page.id}/publish`) : await api.del<Page>(`/pages/${page.id}/publish`);
      qc.setQueryData<Page>(docKeys.page(page.id), (old) => (old ? { ...old, publishedAt: fresh.publishedAt } : old));
      void qc.invalidateQueries({ queryKey: ['pages', 'news'] });
      refreshPages(qc);
      toast(t(publish ? 'docs.news.published' : 'docs.news.unpublished'));
    } catch (e) {
      report(e);
    }
  };
  if (!page.publishedAt) {
    return (
      <div className="banner pg-post-bar" data-state="draft">
        <Newspaper size={16} />
        <span>{t('docs.news.draftHint')}</span>
        {canEdit && (
          <button className="btn sm primary" onClick={() => void set(true)}>
            <Send size={14} /> {t('docs.news.publish')}
          </button>
        )}
      </div>
    );
  }
  return (
    <div className="banner pg-post-bar" data-state="published">
      <Newspaper size={16} />
      <span>{t('docs.news.publishedAt', { date: new Date(page.publishedAt).toLocaleString() })}</span>
      {canEdit && (
        <button className="btn sm ghost" onClick={() => void set(false)}>
          <Undo2 size={14} /> {t('docs.news.unpublish')}
        </button>
      )}
    </div>
  );
}
